import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/commerce-php/commerce-php/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "object-manager"
    }}>{`Object manager`}</h1>
    <p>{`Large applications, such as the Adobe Commerce and Magento Open Source applications, use an object manager to avoid boilerplate code when composing objects during instantiation.`}</p>
    <p>{`In Adobe Commerce and Magento Open Source, the implementation of the `}<a parentName="p" {...{
        "href": "https://github.com/magento/magento2/blob/2.4/lib/internal/Magento/Framework/ObjectManagerInterface.php"
      }}><inlineCode parentName="a">{`ObjectManagerInterface`}</inlineCode></a>{` performs the duties of an object manager.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`The application prohibits the direct use of the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` in your code because it hides the real dependencies of a class.
See `}<a parentName="p" {...{
        "href": "#usage-rules"
      }}>{`usage rules`}</a>{`.`}</p>
    <h2 {...{
      "id": "responsibilities"
    }}>{`Responsibilities`}</h2>
    <p>{`The object manager has the following responsibilities:`}</p>
    <ul>
      <li parentName="ul">{`Object creation in factories and proxies`}</li>
      <li parentName="ul">{`Implementing the singleton pattern by returning the same shared instance of a class when requested`}</li>
      <li parentName="ul">{`Dependency management by instantiating the preferred class when a constructor requests its interface`}</li>
      <li parentName="ul">{`Automatically instantiating parameters in class constructors`}</li>
    </ul>
    <h2 {...{
      "id": "configuration"
    }}>{`Configuration`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "../../build/dependency-injection-file.md"
      }}><inlineCode parentName="a">{`di.xml`}</inlineCode></a>{` file configures the object manager and tells it how to handle `}<a parentName="p" {...{
        "href": "../dependency-injection.md"
      }}>{`dependency injection`}</a>{`.`}</p>
    <p>{`This file specifies the preferred implementation class the object manager generates for the interface declared in a class constructor.
The file also specifies whether the object manager should create an object for every request or treat the object as a singleton.`}</p>
    <h2 {...{
      "id": "usage-rules"
    }}>{`Usage rules`}</h2>
    <p>{`Adobe Commerce and Magento Open Source use the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` to generate and inject the classes declared in your constructor.
Classes should not ask for the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` itself as a constructor dependency.`}</p>
    <p>{`You do not call the object manager directly because the framework handles this automatically.
Direct use of the `}<inlineCode parentName="p">{`create`}</inlineCode>{` function prevents type validation and type hinting that a `}<a parentName="p" {...{
        "href": "../factories.md"
      }}>{`factory`}</a>{` class provides.`}</p>
    <p>{`Object creation is also a separate responsibility that should be moved to a dedicated class such as a `}<a parentName="p" {...{
        "href": "../factories.md"
      }}>{`factory`}</a>{` or `}<a parentName="p" {...{
        "href": "../proxies.md"
      }}>{`proxy`}</a>{`.
In most cases, the framework generates these classes automatically during code compilation.`}</p>
    <InlineAlert variant="warning" slots="text" mdxType="InlineAlert" />
    <p>{`You may notice in the Adobe Commerce and Magento Open Source codebase that some core classes still call the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` directly.
This code needs porting or exist for backward compatibility purposes.
They are not tacit endorsements of using the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` directly.`}</p>
    <h3 {...{
      "id": "exceptions"
    }}>{`Exceptions`}</h3>
    <p>{`You can depend on and use the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` class in the following scenarios:`}</p>
    <ul>
      <li parentName="ul">{`You can use the object manager in static magic methods like `}<inlineCode parentName="li">{`__wakeup()`}</inlineCode>{`, `}<inlineCode parentName="li">{`__sleep()`}</inlineCode>{`, etc.`}
        <ul parentName="li">
          <li parentName="ul">{`An example can be found in the `}<inlineCode parentName="li">{`__wakeup()`}</inlineCode>{` method in the `}<a parentName="li" {...{
              "href": "https://github.com/magento/magento2/blob/2.4/app/code/Magento/Eav/Model/Entity/Attribute/AbstractAttribute.php"
            }}><inlineCode parentName="a">{`Magento/Eav/Model/Entity/Attribute/AbstractAttribute`}</inlineCode></a>{` class.`}</li>
        </ul>
      </li>
      <li parentName="ul">{`You can use the `}<inlineCode parentName="li">{`ObjectManager`}</inlineCode>{` to maintain backward compatibility for a constructor.`}</li>
      <li parentName="ul">{`In a global scope, like in fixtures of integration tests, you can use the object manager.`}</li>
      <li parentName="ul">{`The object manager can be a dependency in classes used for creating objects such as factories or proxies.`}</li>
    </ul>
    <h3 {...{
      "id": "programmatic-product-updates"
    }}>{`Programmatic product updates`}</h3>
    <p>{`If the Access Control List (ACL) rules contain incorrect settings, scripts that update products will fail validation.
This results in products assigned to the wrong store view with the wrong status.`}</p>
    <p>{`To skip ACL validations, use the `}<inlineCode parentName="p">{`ObjectManager`}</inlineCode>{` to set a dynamic dependency injection preference for `}<inlineCode parentName="p">{`Magento\\Framework\\Authorization\\PolicyInterface`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`$objectManager = $bootstrap->getObjectManager();
$objectManager->configure([
    'preferences' => [
        'Magento\\Framework\\Authorization\\PolicyInterface' => 'Magento\\Framework\\Authorization\\Policy\\DefaultPolicy'
    ]
]);
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      